import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { AuthShell, authInputStyle, authInputFocus, authInputBlur, authLabelStyle, authButtonStyle } from '../components/AuthShell';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const { verifyEmail, resendVerification } = useAuthContext();

  useEffect(() => {
    if (!token) { setState('no-token'); return; }
    const verify = async () => {
      try {
        const result = await verifyEmail({ token });
        setState(result.success ? 'success' : 'error');
        if (!result.success) setErrorMessage(result.error || 'Verification failed');
      } catch (err: any) {
        setState('error');
        setErrorMessage(err.message || 'Verification failed');
      }
    };
    verify();
  }, [token, verifyEmail]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) { setResendMessage('Please enter your email'); return; }
    setResendLoading(true);
    setResendMessage(null);
    try {
      const result = await resendVerification(resendEmail);
      setResendMessage(result.success ? 'Verification email sent! Check your inbox.' : (result.error || 'Failed to send'));
    } catch (err: any) {
      setResendMessage(err.message || 'Failed to send');
    } finally {
      setResendLoading(false);
    }
  };

  const resendForm = (
    <form onSubmit={handleResend} style={{ marginTop: 24 }}>
      <p style={{ color: '#888', fontSize: 13, marginBottom: 16, textAlign: 'center' }}>
        Need a new verification link? Enter your email below.
      </p>
      <div style={{ marginBottom: 14 }}>
        <label style={authLabelStyle}>Email</label>
        <input type="email" required value={resendEmail} onChange={e => setResendEmail(e.target.value)}
          placeholder="Enter your email" style={authInputStyle} onFocus={authInputFocus} onBlur={authInputBlur} />
      </div>
      <button type="submit" disabled={resendLoading} style={authButtonStyle(resendLoading)}>
        {resendLoading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : 'Resend Verification Email'}
      </button>
      {resendMessage && (
        <div style={{
          marginTop: 12, padding: '10px 14px', borderRadius: 10, fontSize: 13,
          ...(resendMessage.includes('sent')
            ? { background: 'rgba(40,167,69,0.1)', border: '1px solid #28a745', color: '#28a745' }
            : { background: 'rgba(229,115,115,0.1)', border: '1px solid #e57373', color: '#e57373' })
        }}>
          {resendMessage}
        </div>
      )}
    </form>
  );

  return (
    <>
      <SEO title="Verify Email - RRD Planners" description="Verify your email address" />
      <AuthShell>
        {state === 'loading' && (
          <div style={{ textAlign: 'center' }}>
            <Loader2 size={48} className="animate-spin" style={{ color: '#FFD93D', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Verifying your email...</h2>
            <p style={{ color: '#888', fontSize: 14 }}>Please wait.</p>
          </div>
        )}

        {state === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} style={{ color: '#28a745', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Email Verified!</h2>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 24 }}>Your email has been verified. You can now sign in.</p>
            <a href="/auth/login" style={{
              display: 'inline-block', padding: '12px 32px', background: '#FFD93D', color: '#1a1a1a',
              borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 15
            }}>
              Go to Sign In
            </a>
          </div>
        )}

        {state === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <AlertTriangle size={48} style={{ color: '#e57373', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Verification Failed</h2>
            <p style={{ color: '#e57373', fontSize: 14, marginBottom: 8 }}>{errorMessage}</p>
            {resendForm}
          </div>
        )}

        {state === 'no-token' && (
          <div style={{ textAlign: 'center' }}>
            <AlertTriangle size={48} style={{ color: '#e57373', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Invalid Link</h2>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 8 }}>This verification link is invalid or missing.</p>
            {resendForm}
          </div>
        )}

        <p style={{ color: '#666', fontSize: 14, marginTop: 24, textAlign: 'center' }}>
          <a href="/auth/login" style={{ color: '#FFD93D', fontWeight: 600, textDecoration: 'none' }}>Back to Sign In</a>
        </p>
      </AuthShell>
    </>
  );
};

export default VerifyEmailPage;
